import { useCallback, useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { Toolbar } from "@/organisms/Toolbar"
import { CompaniesForm } from "@/components/CompaniesForm/CompaniesForm"
import { Company } from "@/types/company"
import { getMentoringsV2 } from "@/services/mentoringServices"
import { MentoringDataType } from "@/types/mentoring"
import LoadingOverlay from "@/components/LoadingOverlay"
import { BasicListItem } from "@/components/molecules/BasicListItem"
import { Avatar, Text } from "@/components/atoms"
import { Chip } from "@/components/molecules"
import { cnpjMask } from "@/utils/masks"

const CompanyDetail = () => {
  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState(true)
  const [isFetchingMore, setIsFetchingMore] = useState(false) // Estado para load incremental
  const [mentorings, setMentorings] = useState<MentoringDataType[]>([])
  const [company, setCompany] = useState<Company | null>(null)
  const [meta, setMeta] = useState<any>(null)

  // Função unificada para buscar dados
  const fetchData = useCallback(
    async (pageToFetch: number = 1) => {
      if (!id) return

      try {
        if (pageToFetch === 1) setLoading(true)
        else setIsFetchingMore(true)

        const response = await getMentoringsV2({
          empId: id,
          page: pageToFetch,
          limit: 10,
        })

        if (response.success) {
          // Se for a página 1, substitui. Se for > 1, concatena.
          setMentorings((prev) => (pageToFetch === 1 ? response.data : [...prev, ...response.data]))

          setMeta(response.meta)

          // Só atualizamos os dados da empresa no load inicial
          if (pageToFetch === 1) {
            setCompany(response.meta.empresa)
          }
        }
      } catch (error) {
        console.error("Error loading company details:", error)
      } finally {
        setLoading(false)
        setIsFetchingMore(false)
      }
    },
    [id]
  )

  // Carga inicial
  useEffect(() => {
    fetchData(1)
  }, [fetchData])

  const breadcrumbItems = [
    { href: "/", label: "Início" },
    { href: "/companies", label: "Empresas" },
    {
      href: `/empresas/${id}`,
      label: company?.nomeFantasia || "Carregando...",
      isCurrent: true,
    },
  ]

  if (loading && !company) {
    return <LoadingOverlay />
  }

  return (
    <section className="relative flex h-full flex-col gap-4 duration-500 animate-in fade-in">
      <div className="flex items-center gap-4">
        <Toolbar breadcrumbItems={breadcrumbItems} />
      </div>

      <div className="flex h-full flex-col gap-4 px-8 pt-20">
        {/* Cabeçalho da Empresa */}
        <div className="grid grid-cols-12 grid-rows-2 gap-2">
          <div className="col-span-6 row-span-2 flex flex-col justify-between overflow-hidden rounded-[8px] border border-white/5 bg-dark-500 p-5">
            <div className="flex items-center gap-4">
              <Avatar
                size="lg"
                variant="primary"
                alt={company?.nomeFantasia}
                src={company?.empLogoUrl}
              />
              <div>
                <Text variant="h5">{company?.nomeFantasia}</Text>
                <Text
                  variant="body"
                  className="text-dark-300"
                >
                  {company?.cnpj ? cnpjMask(company.cnpj) : ""}
                </Text>
              </div>
            </div>

            <div className="flex justify-end">
              <Chip variant={company?.statusEmpresa === 1 ? "success" : "error"}>
                {company?.statusEmpresa === 1 ? "Ativo" : "Inativo"}
              </Chip>
            </div>
          </div>

          {/* Cards de Métricas */}
          <div className="col-span-6 flex gap-2">
            <div className="flex-1">
              <BasicListItem
                title="Finalizadas"
                subtitle="In Company"
                value={company?.qtdPresencialFinalizadas || 0}
                icon="land-layer-location"
                color="success"
              />
            </div>
            <div className="flex-1">
              <BasicListItem
                title="Finalizadas"
                subtitle="Ment. Online"
                color="success"
                value={company?.qtdOnlineFinalizadas || 0}
                icon="clapperboard-play"
              />
            </div>
          </div>

          <div className="col-span-6 flex gap-2">
            <div className="flex-1">
              <BasicListItem
                title="Agendadas"
                subtitle="Ment. Online"
                icon="clapperboard-play"
                color="warning"
                value={company?.qtdOnlinePendentes || 0}
              />
            </div>
            <div className="flex-1">
              <BasicListItem
                title="Disponíveis"
                subtitle="Ment. Online"
                icon="clapperboard-play"
                color="info"
                value={company?.qtdOnlineDisponiveis || 0}
              />
            </div>
          </div>
        </div>

        <CompaniesForm
          companyId={id!}
          company={company}
          meta={meta}
          companyMentorId={company?.mentorId}
          mentorings={mentorings}
          onRefresh={() => fetchData(1)}
          onLoadMore={fetchData}
        />

        {isFetchingMore && (
          <div className="fixed bottom-4 right-8 animate-bounce rounded-full bg-primary px-4 py-2 shadow-lg">
            <Text
              variant="label"
              className="text-white"
            >
              Carregando mais mentorias...
            </Text>
          </div>
        )}
      </div>
    </section>
  )
}

export default CompanyDetail
